import { create } from 'zustand';
import type { MediaType, Preferences } from '@teaplus/shared';

export interface StreamTarget {
  type: MediaType;
  mediaId: string;
  title: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  year: number | null;
  season?: number;
  episode?: number;
  episodeTitle?: string | null;
  restart?: boolean;
}

interface UiState {
  streamTarget: StreamTarget | null;
  openStreams: (t: StreamTarget) => void;
  closeStreams: () => void;
  /** Appearance changes being tried in the Preview sidebar, unsaved. Null when it's closed. */
  lookDraft: Partial<Preferences> | null;
  setLookDraft: (draft: Partial<Preferences> | null) => void;
}

export const useUi = create<UiState>((set) => ({
  streamTarget: null,
  openStreams: (t) => set({ streamTarget: t }),
  closeStreams: () => set({ streamTarget: null }),
  lookDraft: null,
  setLookDraft: (lookDraft) => set({ lookDraft }),
}));

export interface LastStream {
  addonId: string;
  fingerprint: string;
  label: string;
  at: number;
}
const lastKey = (mediaId: string, season?: number, episode?: number) =>
  `tp:last:${mediaId}${season != null ? `:${season}:${episode}` : ''}`;
export const streamFingerprint = (s: { addonId: string; filename: string | null; title: string }) =>
  `${s.addonId}|${(s.filename ?? s.title).toLowerCase()}`;
export const lastStream = {
  get(mediaId: string, season?: number, episode?: number): LastStream | null {
    try {
      return JSON.parse(
        localStorage.getItem(lastKey(mediaId, season, episode)) ?? 'null',
      ) as LastStream | null;
    } catch {
      return null;
    }
  },
  set(mediaId: string, season: number | undefined, episode: number | undefined, v: LastStream) {
    try {
      localStorage.setItem(lastKey(mediaId, season, episode), JSON.stringify(v));
    } catch {}
  },
};

export type StreamSort = 'quality' | 'size';
/** The heading the stream table is sorted by, remembered on this device. */
export const streamSort = {
  get(): StreamSort {
    try {
      const v = localStorage.getItem('tp:stream-sort');
      return v === 'size' ? v : 'quality';
    } catch {
      return 'quality';
    }
  },
  set(v: StreamSort) {
    try {
      localStorage.setItem('tp:stream-sort', v);
    } catch {}
  },
};

export const lastSource = {
  get(mediaId: string): string | null {
    try {
      return sessionStorage.getItem(`tp:src:${mediaId}`);
    } catch {
      return null;
    }
  },
  set(mediaId: string, addonId: string) {
    try {
      sessionStorage.setItem(`tp:src:${mediaId}`, addonId);
    } catch {}
  },
};
